import { useState, type ReactNode } from 'react'
import { Badge, Box, Button, HStack, Text, VStack } from '@chakra-ui/react'
import { FiNavigation } from 'react-icons/fi'
import { firstOpenResident, stopAlreadyVoted, type WalkStop } from '../../../lib/walkStops'
import { directionsUrl } from '../../../lib/walkGeo'
import { WorksiteInfoSheet } from './WorksiteInfoSheet'

const MAX_CHIPS = 4

function Shell({ children }: { children: ReactNode }) {
  // The bottom nav is gone, so the dock is the bottom edge (viewport-fit=cover): clear the home indicator.
  return (
    <Box data-testid="next-door-dock" bg="surface" borderTopWidth="1px" borderColor="border" px={4} pt={3}
      pb="calc(12px + env(safe-area-inset-bottom, 0px))" shadow="md">
      {children}
    </Box>
  )
}

/**
 * [Spec §4.4] The next-door dock: the one stop the canvasser should act on now.
 * Directions is a plain external link — no copy/share affordances (noExport.guard).
 * The schedule-based "Likely at work" label is deliberately NOT carried over (Decision Log 2026-10-09).
 */
export function NextDoorDock({
  stop, sequenceNumber, isSuggested, distanceLabel, travelMode, personFirst, personId, revisitCount,
  onAtDoor, onDoneHere, onShowRevisits,
}: {
  stop: WalkStop | null
  sequenceNumber: number | null
  isSuggested: boolean
  distanceLabel: string
  travelMode: 'walking' | 'driving'
  personFirst: boolean
  /** Person-first: the person tapped in the Contacts view. Falls back to the first open resident. */
  personId?: string | null
  revisitCount: number
  /** Called with the stop and the person "I'm at the door" opens — the one the dock names. */
  onAtDoor: (stop: WalkStop, contactId: string) => void
  onDoneHere: (stop: WalkStop) => void
  onShowRevisits: () => void
}) {
  const [infoOpen, setInfoOpen] = useState(false)
  if (!stop) {
    return (
      <Shell>
        <VStack gap={2} py={2}>
          <Text fontWeight="semibold">Nothing left on this turf</Text>
          {revisitCount > 0 && (
            <Button minH="48px" variant="outline" borderColor="border" onClick={onShowRevisits}>
              {`Show ${revisitCount} to revisit`}
            </Button>
          )}
        </VStack>
      </Shell>
    )
  }

  const worksite = stop.kind === 'worksite'
  const n = sequenceNumber ?? ''
  const eyebrow = !isSuggested ? `Selected · ${n}` : worksite ? `Next stop · ${n}` : `Next door · ${n}`
  // The person the door opens: in person-first mode the tapped person (never a housemate), else the
  // first open resident. The title always names exactly who "I'm at the door" will open.
  const tapped = personFirst && personId ? stop.residents.find(r => r.contactId === personId) : undefined
  const person = tapped ?? firstOpenResident(stop)
  const title = personFirst && person
    ? `Next: ${`${person.firstName} ${person.lastName ?? ''}`.trim()}`
    : worksite && stop.workplaceLabel ? stop.workplaceLabel : null
  const chips = stop.residents.slice(0, MAX_CHIPS).map(r => r.firstName)
  const extra = stop.residents.length - chips.length
  const lat = stop.lat
  const lng = stop.lng

  return (
    <Shell>
      <VStack align="stretch" gap={2}>
        <Text fontSize="xs" fontWeight="bold" color="accent" textTransform="uppercase" letterSpacing="wider">{eyebrow}</Text>
        {title && <Text fontWeight="bold" fontSize="lg">{title}</Text>}
        <HStack justify="space-between" align="baseline" gap={2}>
          <Text fontWeight={title ? 'medium' : 'bold'} fontSize={title ? 'md' : 'lg'} lineClamp={1}>{stop.address}</Text>
          {distanceLabel && <Text fontSize="sm" color="fg.muted" flexShrink={0}>{distanceLabel}</Text>}
        </HStack>
        {stop.movedFrom && <Text fontSize="sm" color="warning.fg">{`Moved from ${stop.movedFrom}`}</Text>}
        {/* [CF-1 P5] Person-first reads the person the dock names; a door, everyone at it. */}
        {stopAlreadyVoted(stop, personFirst ? person?.contactId : null) && (
          <Badge alignSelf="start" bg="info.subtle" color="info.fg" whiteSpace="normal">Already voted — skip</Badge>
        )}
        {worksite ? (
          personFirst && stop.workplaceLabel && <Text color="fg.muted">{stop.workplaceLabel}</Text>
        ) : (
          <HStack gap={1} wrap="wrap">
            {chips.map((name, i) => (
              <Box key={`${name}-${i}`} as="span" px={2} py="2px" borderRadius="full" bg="accent.soft" color="accent" fontSize="sm">
                {name}
              </Box>
            ))}
            {extra > 0 && <Text as="span" fontSize="sm" color="fg.muted">{`+${extra}`}</Text>}
          </HStack>
        )}
        <HStack gap={2} pt={1}>
          <Button flex={1} minH="48px" bg="accent" color="accent.fg" onClick={() => onAtDoor(stop, person.contactId)}>
            {worksite ? "I'm here" : "I'm at the door"}
          </Button>
          {worksite && (
            <Button minH="48px" variant="outline" borderColor="border" onClick={() => onDoneHere(stop)}>
              Done here
            </Button>
          )}
          {lat != null && lng != null && (
            <Button asChild minH="48px" variant="outline" borderColor="border">
              <a href={directionsUrl(lat, lng, travelMode)} target="_blank" rel="noopener noreferrer">
                <FiNavigation aria-hidden />
                Directions
              </a>
            </Button>
          )}
        </HStack>
        {worksite && (
          <>
            <Button minH="44px" variant="outline" borderColor="border" onClick={() => setInfoOpen(true)}>Worksite info</Button>
            <WorksiteInfoSheet open={infoOpen} onClose={() => setInfoOpen(false)} workplaceLabel={stop.workplaceLabel}
              address={stop.address} info={stop.worksiteInfo} />
          </>
        )}
      </VStack>
    </Shell>
  )
}
